import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react'

import { IOrder } from 'checkout/types/IOrder'
import { Order } from 'shared/types/Order'
import { t } from 'localization'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'

const TP = 'checkout.containers.OrderSummaryContextProvider'

type OrderSummaryState = {
  /** Places order. See method in {@link useOrderRTK} */
  buyOrderRTK: () => void
  /** Changes order between lowest price/F2Y. See method in {@link useOrderRTK} */
  changeOrderOptionRTK: (props) => void
  /** Indicates if fee breakdown section is displaying */
  isFeeBreakdownOpen: boolean
  /** Object representing current order */
  order: IOrder | Order
  /** Indicates if payment for the order is being submitted */
  orderSubmitting: boolean
  /** Indicates if all checks passed for the order to be placed */
  orderReadyForPurchase: boolean
  /** Places order. See method in {@link useOrder} */
  purchaseOrder: () => void
  /** Sets the value of isFeeBreakdownOpen which indicates if fee breakdown section is displaying */
  setIsFeeBreakdownOpen: Dispatch<SetStateAction<boolean>>
  /** Indicates if loading spinner should be displayed on order summary panel */
  showSpinner: boolean
  /** Text to be displayed while spinner is displayed on order summary panel */
  spinnerText: string
}

export const OrderSummaryContext = createContext<OrderSummaryState>({
  buyOrderRTK: () => null,
  changeOrderOptionRTK: () => null,
  isFeeBreakdownOpen: false,
  order: {},
  orderSubmitting: false,
  orderReadyForPurchase: false,
  purchaseOrder: () => null,
  setIsFeeBreakdownOpen: () => null,
  showSpinner: false,
  spinnerText: '',
})

export const useOrderSummaryContext = () => useContext(OrderSummaryContext)

type OrderSummaryContextProviderProps = {
  children: ReactNode
}

export const OrderSummaryContextProvider = ({ children }: OrderSummaryContextProviderProps) => {
  const [isFeeBreakdownOpen, setIsFeeBreakdownOpen] = useState(false)
  const {
    buyOrderRTK,
    changeOrderOptionRTK,
    order,
    orderReadyForPurchase,
    orderSubmitting,
    panels: {
      shippingAddress: { loading: shippingAddressLoading },
    },
    purchaseOrder,
  } = useCheckoutContext()

  if (!order) {
    return null
  }

  const spinnerText = shippingAddressLoading
    ? t(`${TP}.updatingPrice`, 'Updating price...')
    : t(`${TP}.placingOrder`, 'Placing order...')
  const showSpinner = orderSubmitting || shippingAddressLoading

  const orderSummaryContextValues: OrderSummaryState = {
    buyOrderRTK,
    changeOrderOptionRTK,
    isFeeBreakdownOpen,
    order,
    orderSubmitting,
    orderReadyForPurchase,
    purchaseOrder,
    setIsFeeBreakdownOpen,
    showSpinner,
    spinnerText,
  }

  return (
    <OrderSummaryContext.Provider value={orderSummaryContextValues}>
      {children}
    </OrderSummaryContext.Provider>
  )
}
